const RELOAD_KEY = "chunk-error-reload";
const RELOAD_COUNT_KEY = "chunk-error-reload-count";
const THROTTLE_MS = 30_000;
const MAX_RELOAD_ATTEMPTS = 3;
const COUNTER_TTL_MS = 5 * 60_000;

/** Shared detection primitive — works on raw strings (no Error object needed). */
function isChunkLoadMessage(name: string, message: string): boolean {
    return (
        name === "ChunkLoadError" ||
        message.includes("Loading chunk") ||
        message.includes("Failed to fetch dynamically imported module")
    );
}

/**
 * Detects whether an error is a ChunkLoadError (failed lazy-load of a JS chunk).
 * Covers webpack ChunkLoadError, ESM dynamic import failures, and generic "Loading chunk" messages.
 */
export function isChunkLoadError(error: Error): boolean {
    return isChunkLoadMessage(error.name, error.message);
}

type ReloadFn = () => void;

interface RecoveryOptions {
    /** Custom reload function (defaults to window.location.reload). Useful for testing. */
    reload?: ReloadFn;
    /** Whether to reload in catch block when sessionStorage is unavailable (default: true). */
    reloadOnStorageError?: boolean;
}

/**
 * Attempts to recover from a chunk load error by reloading the page.
 *
 * Throttle: max 1 reload per 30s via sessionStorage timestamp.
 * Hard limit: max 3 reload attempts — after that, shows fallback UI.
 * Counter resets after 5 minutes of no chunk errors (handles multiple deployments per session).
 *
 * Returns true if the error was a chunk error (caller should stop propagation).
 */
function tryReload(opts: RecoveryOptions): boolean {
    const reload = opts.reload ?? (() => window.location.reload());
    const reloadOnStorageError = opts.reloadOnStorageError ?? true;

    try {
        const lastReload = sessionStorage.getItem(RELOAD_KEY);
        let count = Number(sessionStorage.getItem(RELOAD_COUNT_KEY)) || 0;
        const now = Date.now();
        const lastReloadTs = Number(lastReload);

        if (lastReload && Number.isFinite(lastReloadTs) && now - lastReloadTs > COUNTER_TTL_MS) {
            count = 0;
            sessionStorage.removeItem(RELOAD_COUNT_KEY);
        }

        if (count >= MAX_RELOAD_ATTEMPTS) return true;

        if (!lastReload || !Number.isFinite(lastReloadTs) || now - lastReloadTs > THROTTLE_MS) {
            sessionStorage.setItem(RELOAD_KEY, String(now));
            sessionStorage.setItem(RELOAD_COUNT_KEY, String(count + 1));
            reload();
        }
    } catch {
        if (reloadOnStorageError) {
            reload();
        }
    }

    return true;
}

/**
 * Detects ChunkLoadError from raw error handler arguments (handles non-Error rejection reasons).
 * Triggers a page reload to recover, with throttle + hard limit.
 * Returns true if the error was a chunk error (caller should stop propagation).
 */
export function handleChunkLoadError(message: unknown, error?: unknown, opts?: RecoveryOptions): boolean {
    const msg = typeof message === "string" ? message : "";
    const errorMsg = error instanceof Error ? error.message : "";
    const errorName = error instanceof Error ? error.name : "";

    if (!isChunkLoadMessage(errorName, msg || errorMsg)) return false;

    return tryReload({ reloadOnStorageError: true, ...opts });
}

/**
 * Auto-reload logic for use inside error boundary useEffect.
 * Does NOT reload when sessionStorage is unavailable — shows fallback UI instead.
 */
export function attemptChunkErrorRecovery(error: Error, opts?: RecoveryOptions): void {
    if (!isChunkLoadError(error)) return;

    tryReload({ reloadOnStorageError: false, ...opts });
}
